import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        const token = localStorage.getItem("tb_token");
        if (!token) { setUser(null); setLoading(false); return; }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (err) {
            // Only drop the token when the server explicitly rejects it.
            // Network errors, timeouts, and 5xx are transient — keep the token
            // so a page refresh or reconnect can recover the session instead of
            // logging the user out on every hiccup.
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                localStorage.removeItem("tb_token");
                setUser(null);
            }
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("tb_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (name, email, password, acceptedTerms = false) => {
        const { data } = await api.post("/auth/register", { name, email, password, accepted_terms: acceptedTerms });
        localStorage.setItem("tb_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const googleLogin = useCallback(async (sessionId) => {
        const { data } = await api.post("/auth/google-session", { session_id: sessionId });
        localStorage.setItem("tb_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("tb_token");
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, googleLogin, refresh, setUser }),
        [user, loading, login, register, logout, googleLogin, refresh]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
